import {
  Mesh,
  SphereGeometry,
  TorusGeometry,
  PlaneGeometry,
  Vector3
} from 'three';
import { Ability } from './Ability.js';
import { settings } from '../config/settings.js';
import { ParticleShape } from '../particles/ParticleSystem.js';
import { RateEmitter } from '../particles/ParticleEngine.js';
import { BurstMode } from '../effects/BurstSphere.js';
import { DecalType } from '../effects/GroundDecals.js';
import { frame } from '../core/FrameUniforms.js';
import { LAYER } from '../core/Layers.js';
import { saturate, Easing } from '../utils/math.js';
import { getColor } from '../utils/color.js';
import {
  ConstructStyle,
  ConstructGlyph,
  createConstructMaterial,
  createConstructGlyphMaterial,
  syncConstructMaterial
} from '../materials/ConstructMaterial.js';
import { pointOnRing } from './ConstructUtils.js';

const DROPLETS = 18;
const _pos = new Vector3();
const _dir = new Vector3();
const _emit = {};

/**
 * TIDAL VAULT — a temporary volume that rises, suspends and drops.
 *
 * Most zones in the sandbox threaten their boundary. This one makes its whole
 * interior the event: a hollow water dome lifts from the floor, holds a field
 * of droplets unnaturally still, then loses support and slams down as one mass.
 * In combat it reads as displacement/setup followed by a delayed knock-down.
 */
export class TidalVaultAbility extends Ability {
  constructor(context) {
    super('tide', context);
  }

  createShaders() {
    this.domeGeometry = new SphereGeometry(1, 48, 24, 0, Math.PI * 2, 0, Math.PI / 2);
    this.waterMaterial = createConstructMaterial(ConstructStyle.WATER, { normal: true });
    this.dome = new Mesh(this.domeGeometry, this.waterMaterial);
    this.dome.layers.set(LAYER.VFX);
    this.dome.renderOrder = 10;
    this.group.add(this.dome);

    this.rimGeometry = new TorusGeometry(1, 0.035, 8, 72);
    this.rimMaterial = createConstructMaterial(ConstructStyle.ENERGY);
    this.rim = new Mesh(this.rimGeometry, this.rimMaterial);
    this.rim.layers.set(LAYER.VFX);
    this.rim.renderOrder = 12;
    this.rim.rotation.x = Math.PI / 2;
    this.group.add(this.rim);

    this.fieldGeometry = new PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
    this.fieldMaterial = createConstructGlyphMaterial(ConstructGlyph.WATER);
    this.field = new Mesh(this.fieldGeometry, this.fieldMaterial);
    this.field.layers.set(LAYER.VFX);
    this.field.renderOrder = 8;
    this.group.add(this.field);

    this.dropGeometry = new SphereGeometry(1, 10, 7);
    this.dropMaterial = createConstructMaterial(ConstructStyle.WATER, { normal: true });
    this.droplets = [];
    for (let i = 0; i < DROPLETS; i++) {
      const mesh = new Mesh(this.dropGeometry, this.dropMaterial);
      mesh.layers.set(LAYER.VFX);
      mesh.renderOrder = 11;
      this.group.add(mesh);
      this.droplets.push({
        mesh,
        angle: i / DROPLETS * Math.PI * 2 + Math.sin(i * 12.31) * 0.4,
        radius: 0.2 + ((i * 37) % 17) / 20,
        height: 0.18 + ((i * 19) % 13) / 14,
        size: 0.45 + ((i * 23) % 11) / 15
      });
    }

    this.centre = new Vector3();
    this._seed = 0;
    this._dropped = false;
  }

  createParticles() {
    this.spray = this.ctx.particles.get('tide.spray', {
      capacity: 4300,
      shape: ParticleShape.STREAK,
      additive: true,
      stretch: true,
      softFade: 0.4
    });
    this.spray.uniforms.uGravity.value.set(0, -9, 0);
    this.spray.uniforms.uDrag.value = 0.35;
    this.spray.uniforms.uEndSize.value = 0.14;
    this.spray.uniforms.uFadeOut.value = 0.5;

    this.mist = this.ctx.particles.get('tide.mist', {
      capacity: 2400,
      shape: ParticleShape.SMOKE,
      additive: false,
      curl: true,
      softFade: 1.0
    });
    this.mist.uniforms.uGravity.value.set(0, 0.35, 0);
    this.mist.uniforms.uDrag.value = 1.8;
    this.mist.uniforms.uEndSize.value = 2.4;
    this.mist.uniforms.uFadeOut.value = 0.32;
    this.mistEmitter = new RateEmitter();
  }

  get impactDuration() {
    const c = settings.tide;
    return Math.max(0.2, (c.riseTime + c.holdTime + c.dropTime) * settings.global.lifetime);
  }

  get fadeDuration() {
    return Math.max(0.1, settings.tide.fadeTime);
  }

  get instanceCount() {
    return DROPLETS + 3;
  }

  onSpawn() {
    this._seed = Math.random() * 10;
    this._dropped = false;
    this.mistEmitter.reset();
    this.dome.visible = true;
    this.rim.visible = false;
    this.field.visible = false;
    for (const drop of this.droplets) drop.mesh.visible = false;
    this._syncSeed();
  }

  _syncSeed() {
    const c = settings.tide;
    this.dome.position.copy(this.position).setY(c.travelHeight);
    this.dome.scale.setScalar(c.seedSize);
    syncConstructMaterial(this.waterMaterial, c, {
      seed: this._seed,
      reveal: 1,
      fade: 1,
      pulse: 0.4
    });
  }

  onTravel(dt) {
    this._syncSeed();
    const c = settings.tide;
    const count = this.mistEmitter.tick(dt, c.trailRate);
    if (count > 0) this._emit(this.dome.position, count, _dir.copy(this.direction).multiplyScalar(-1), c.trailSpeed, this.spray);
  }

  onImpact() {
    const c = settings.tide;
    this.pointAt(1, this.centre).setY(0);
    this.position.copy(this.centre).setY(c.height * 0.45);
    this.rim.visible = true;
    this.field.visible = true;
    for (const drop of this.droplets) drop.mesh.visible = true;
    this.lightBoost = c.arrivalLight;

    this.ctx.decals.spawn(DecalType.RIPPLE, this.centre, {
      radius: c.zoneRadius,
      life: c.riseTime + c.holdTime,
      growth: 0.12,
      intensity: c.fieldIntensity,
      width: 0.05,
      colorA: getColor(c.colorA),
      colorB: getColor(c.colorC)
    });
  }

  _emit(position, count, direction, speed, system = this.spray, radius = 0.2) {
    const c = settings.tide;
    const g = settings.global;
    _emit.position = position;
    _emit.radius = radius;
    _emit.direction = direction;
    _emit.speed = speed * g.particleSpeed;
    _emit.speedVariance = 0.55;
    _emit.spread = 0.55;
    _emit.inherit = null;
    _emit.anchor = this.centre;
    _emit.size = c.particleSize * g.particleSize;
    _emit.sizeVariance = 0.65;
    _emit.life = c.particleLife * g.particleLifetime;
    _emit.lifeVariance = 0.4;
    _emit.spin = 1;
    _emit.tint = null;
    _emit.time = frame.uTime.value;
    system.emit(Math.round(count * g.particleCount), _emit);
  }

  _drop() {
    if (this._dropped) return;
    this._dropped = true;
    const c = settings.tide;
    const g = settings.global;
    const surface = _pos.copy(this.centre).setY(c.height * 0.18);

    this.ctx.bursts.spawn(BurstMode.WATER, surface, {
      radius: c.zoneRadius * 0.25,
      endRadius: c.zoneRadius * c.burstRadius,
      life: c.burstLife,
      intensity: c.burstIntensity,
      squash: 0.35,
      colorA: getColor(c.colorC),
      colorB: getColor(c.colorA),
      colorC: getColor(c.colorB)
    });
    this.ctx.decals.spawn(DecalType.RIPPLE, this.centre, {
      radius: c.zoneRadius * 1.4,
      life: c.rippleLife,
      growth: c.rippleGrowth,
      intensity: c.burstIntensity,
      width: 0.035,
      colorA: getColor(c.colorC),
      colorB: getColor(c.colorA)
    });
    this._emit(surface, c.dropParticles, _dir.set(0, -1, 0), c.dropSpeed, this.spray, c.zoneRadius * 0.7);
    this.lightBoost = c.impactLight;
    this.ctx.shake.add(c.impactShake * g.cameraShake, c.shakeDuration);
    this.ctx.flash.trigger(getColor(c.colorC), c.impactFlash);
  }

  onFade(dt, t) {
    const c = settings.tide;
    const g = settings.global;
    const fade = t <= 1 ? 1 : 1 - (t - 1);
    const lifeScale = g.lifetime;
    const riseEnd = c.riseTime * lifeScale;
    const holdEnd = (c.riseTime + c.holdTime) * lifeScale;
    const dropEnd = (c.riseTime + c.holdTime + c.dropTime) * lifeScale;
    const rise = Easing.outCubic(saturate(this.impactTime / Math.max(0.01, riseEnd)));
    const drop = Easing.inCubic(saturate((this.impactTime - holdEnd) / Math.max(0.01, dropEnd - holdEnd)));
    if (drop > 0.05) this._drop();
    const body = Math.max(0.001, rise * (1 - drop));

    this.dome.position.copy(this.centre).setY(c.baseHeight);
    this.dome.scale.set(c.zoneRadius * body, c.height * body, c.zoneRadius * body);
    this.dome.rotation.y = this.age * c.flow * 0.12;
    syncConstructMaterial(this.waterMaterial, c, {
      seed: this._seed,
      reveal: rise,
      fade: fade * (1 - drop * 0.65),
      pulse: Math.sin(this.age * c.pulseSpeed) * 0.5 + 0.5
    });

    this.rim.position.copy(this.centre).setY(c.rimHeight + drop * c.dropSplashHeight);
    this.rim.rotation.x = Math.PI / 2;
    this.rim.rotation.z = this.age * c.rimSpin;
    this.rim.scale.setScalar(c.zoneRadius * (0.3 + rise * 0.7) * (1 + drop * 0.25));
    syncConstructMaterial(this.rimMaterial, c, {
      seed: this._seed,
      reveal: rise,
      fade: fade * (1 - drop),
      pulse: drop
    });

    const fieldSize = c.zoneRadius * 2.4;
    this.field.position.copy(this.centre).setY(c.fieldHeight);
    this.field.scale.set(fieldSize, fieldSize, fieldSize);
    syncConstructMaterial(this.fieldMaterial, c, {
      seed: this._seed,
      reveal: rise,
      fade,
      pulse: drop
    });

    for (let i = 0; i < this.droplets.length; i++) {
      const record = this.droplets[i];
      const angle = record.angle + this.age * c.dropletOrbit * (i % 2 ? -1 : 1);
      const radius = c.zoneRadius * record.radius * (0.25 + rise * 0.75);
      pointOnRing(record.mesh.position, this.centre, radius, angle, c.height * record.height * rise);
      record.mesh.position.y = c.baseHeight + (record.mesh.position.y - c.baseHeight) * (1 - drop) ** 2;
      record.mesh.scale.setScalar(c.dropletSize * record.size * rise * fade * (1 - drop));
      record.mesh.rotation.set(angle, this.age * c.flow + i, angle * 0.5);
    }
    syncConstructMaterial(this.dropMaterial, c, {
      seed: this._seed + 4,
      reveal: rise,
      fade: fade * (1 - drop),
      pulse: 0.25
    });

    if (rise > 0.5 && drop < 0.8) {
      const count = this.mistEmitter.tick(dt, c.mistRate);
      if (count > 0) {
        pointOnRing(_pos, this.centre, c.zoneRadius * 0.82, Math.random() * Math.PI * 2, 0.12);
        this._emit(_pos, count, _dir.set(0, 1, 0), c.mistSpeed, this.mist, c.mistRadius);
      }
      this.ctx.shake.rumble(c.holdRumble * g.cameraShake * rise, dt);
    }
  }

  onDestroy() {
    this.dome.visible = false;
    this.rim.visible = false;
    this.field.visible = false;
    for (const drop of this.droplets) drop.mesh.visible = false;
  }

  dispose() {
    this.domeGeometry.dispose();
    this.rimGeometry.dispose();
    this.fieldGeometry.dispose();
    this.dropGeometry.dispose();
    this.waterMaterial.dispose();
    this.rimMaterial.dispose();
    this.fieldMaterial.dispose();
    this.dropMaterial.dispose();
    super.dispose();
  }
}
